import React from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router';

import {addDays} from 'date-fns/addDays';
import {differenceInDays} from 'date-fns/differenceInDays';
import {endOfMonth} from 'date-fns/endOfMonth';
import {startOfDay} from 'date-fns/startOfDay';
import {startOfMonth} from 'date-fns/startOfMonth';

import {
  Modal,
  ModalCancelButton,
  ModalHeader,
  ModalSubmitButton,
  NewDatePickerField,
} from '@edna/components';

import {Banner} from 'src/containers/Banner';
import {useBlockSupportClientMutation} from 'src/containers/SupportClients/api';
import {joiResolver} from 'src/utils/validator';

import * as S from './style';

const MAX_TEMPORARY_BLOCKED_DAYS = 120;

const formResolver = joiResolver((joi) =>
  joi.object({
    blockPeriod: joi
      .custom((value, helpers) => {
        const diffsDate = differenceInDays(value.endDate, value.startDate);

        if (diffsDate > MAX_TEMPORARY_BLOCKED_DAYS) {
          return helpers.error('invalidTemporaryBlockDays', {
            maxDays: MAX_TEMPORARY_BLOCKED_DAYS,
          });
        }

        return value;
      })
      .required(),
  }),
);

type TProps = {
  closeModal: TEmptyFunction;
};

const getCalendarPeriod = (period = 0) => {
  const startDate = startOfDay(new Date());
  const endDate = addDays(startDate, period);

  return {
    startDate,
    endDate,
  };
};

const DEFAULT_VALUES = {
  blockPeriod: getCalendarPeriod(30),
};

const DeactivateClientModal = React.memo<TProps>(({closeModal}) => {
  const {t} = useTranslation();
  const {id: supportTenantId} = useParams<'id'>();

  const methods = useForm({
    mode: 'all',
    defaultValues: DEFAULT_VALUES,
    resolver: formResolver,
  });

  const [blockSupportClient] = useBlockSupportClientMutation();

  const handleSubmit = methods.handleSubmit(async (values) => {
    closeModal();

    if (supportTenantId) {
      await blockSupportClient({
        id: supportTenantId,
        startAt: values.blockPeriod.startDate,
        endAt: values.blockPeriod.endDate,
      });
    }
  });

  return (
    <Modal width="430px">
      <ModalHeader>{t(`ClientStatus:deactivateClientModal.title`)}</ModalHeader>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit}>
          <Banner type="WARNING" title={t(`ClientStatus:deactivateClientModal.message`)} />
          <S.Label>{t('ClientStatus:deactivateClientModal.periodLabel')}</S.Label>
          <NewDatePickerField
            name="blockPeriod"
            calendarType="DATE_RANGE"
            minDate={startOfMonth(new Date())}
            maxDate={endOfMonth(addDays(new Date(), 180))}
          />
          <S.ModalActions>
            <ModalCancelButton onClick={closeModal}>{t('Common:button.cancel')}</ModalCancelButton>
            <ModalSubmitButton>{t('ClientStatus:deactivateClientModal.move')}</ModalSubmitButton>
          </S.ModalActions>
        </form>
      </FormProvider>
    </Modal>
  );
});

DeactivateClientModal.displayName = 'DeactivateClientModal';

export default DeactivateClientModal;
